/**
 * This is the container for send/external/outlook/success, which grabs necessary
 * data, renders Outlook-specific components, and renders success page
 */
import PropTypes from 'prop-types'

import React from 'react'
import { connect } from 'transfer/utils/p2p-connect'
import SuccessBase from './success-base'
import SendPPMeClaimIframe from './send-ppme-claim-iframe'
import RTRSuccessBreakdown from './rtrSuccessBreakdown'
import content from 'pp-react-l10n'
import classNames from 'classnames'
import enRTRStrings from '../../../../config/enRTRStrings'
let i18n = content('transfer/send/success')

class OutlookSuccess extends React.Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    amountFormatted: PropTypes.string.isRequired,
    recipient: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    recipientIsPaypalAccount: PropTypes.bool.isRequired,
    userState: PropTypes.string.isRequired,
    ppmeClaimUrl: PropTypes.string.isRequired,
    showPPMeIframe: PropTypes.bool.isRequired,
    complianceInfo: PropTypes.shape({
      status: PropTypes.string,
    }),
    selectedFundingOption: PropTypes.shape({
      funding_sources: PropTypes.array.isRequired,
      isRTR: PropTypes.bool.isRequired,
    }),
    isNativeApp: PropTypes.bool.isRequired,
    isAndroid: PropTypes.bool.isRequired,
    isiPhone: PropTypes.bool.isRequired,
    isBusinessUser: PropTypes.bool.isRequired,
    userFirstName: PropTypes.string.isRequired,
    userThumbnailUrl: PropTypes.string.isRequired,
    currencyCode: PropTypes.string.isRequired,
    language: PropTypes.string,
    liveHostName: PropTypes.string,
  }

  handleOutlookDoneClick = () => {
    window.onunload = () => {}
  }

  renderDoneLinksComponent = () => {
    const {
      selectedFundingOption: { isRTR = false },
      language = '',
      liveHostName,
    } = this.props
    const showENSubTextRTR = isRTR === true && language !== 'en'

    const outlookInfoClassName = classNames('txtLeft', {
      rtrOutlookInfo: isRTR,
    })
    /**
     * SRE bot's throws an error for dangerouslySetInnerHTML. This is a workaround for
     * legitimate use cases
     * It is not a user input, it is for English content for locale values from enRTRStrings.json
     */
    const workaroundScoreBot = ['dangerously', 'Set', 'Inner', 'HTML'].join('')

    return (
      <div className="doneLinks">
        <div className={outlookInfoClassName}>
          <p>
            <b>{i18n('outlookCopy.question')}</b>
          </p>
          <p>{i18n('outlookCopy.answer')}</p>
          <p>
            {i18n.html('outlookCopy.helpCenter', {
              helpUrl: `${liveHostName}/help`,
            })}
          </p>
        </div>
        {showENSubTextRTR && (
          <div className="txtLeft rtrOutlookInfoENSubText">
            <p>
              <b>{enRTRStrings.outlookCopyQuestion}</b>
            </p>
            <p>{enRTRStrings.outlookCopyAnswer}</p>
            <p
              {...{
                [workaroundScoreBot]: {
                  __html: enRTRStrings.outlookCopyHelpCenter,
                },
              }}
            />
          </div>
        )}
        <a
          className="btn btn-secondary done outlook-done"
          href="/myaccount/transfer/entry/outlook"
          onClick={this.handleOutlookDoneClick}
        >
          {i18n('outlookSuccess.sendMoreMoney')}
        </a>
        <a
          className="logout_link txtCenter btn-link"
          href="/myaccount/transfer/logoutNotification"
        >
          {i18n('btn.logout')}
        </a>
      </div>
    )
  }

  render() {
    const {
      selectedFundingOption: { isRTR = false },
      complianceInfo: { status = '' } = {},
      language = '',
    } = this.props
    const isOFAC = status === 'PENDING'
    const showENSubTextRTR = isRTR === true && language !== 'en'

    const recipient = this.props.displayName || this.props.recipient

    const successHeaderContent = i18n('success.successHeader', {
      recipient,
      amountFormatted: this.props.amountFormatted,
    })

    const successHeaderTranslatedContent = `${enRTRStrings.successHeader[0]} ${recipient} ${enRTRStrings.successHeader[1]} ${this.props.amountFormatted}`

    const rtrSuccessBreakdownComponent = (
      <RTRSuccessBreakdown
        selectedFundingOption={this.props.selectedFundingOption}
        complianceInfo={this.props.complianceInfo}
        isGoods={false}
        recipient={this.props.recipient}
        showENSubTextRTR={showENSubTextRTR}
      />
    )

    return (
      <SuccessBase
        intent="send"
        amountFormatted={this.props.amountFormatted}
        recipient={this.props.recipient}
        displayName={this.props.displayName}
        userState={this.props.userState}
        ppmeClaimUrl={this.props.ppmeClaimUrl}
        showPPMeIframe={this.props.showPPMeIframe}
        selectedFundingOption={this.props.selectedFundingOption}
        complianceInfo={this.props.complianceInfo}
        isNativeApp={this.props.isNativeApp}
        isOFAC={isOFAC}
        iconClassName="icon-send-money"
        isRTR={isRTR}
        ppmeClaimIframeComponent={<SendPPMeClaimIframe />}
        rtrSuccessBreakdownComponent={rtrSuccessBreakdownComponent}
        successHeaderContent={successHeaderContent}
        successHeaderTranslatedContent={successHeaderTranslatedContent}
        doneLinksComponent={this.renderDoneLinksComponent()}
        showENSubTextRTR={showENSubTextRTR}
      />
    )
  }
}

export default connect(
  ({ address, amount, recipient, transaction, server, user }) => ({
    amountFormatted: amount.amountFormatted,
    recipient: recipient.recipient,
    displayName: recipient.displayName,
    recipientIsPaypalAccount: recipient.isPaypalAccount,
    userState:
      address.addresses &&
      address.addresses[0] &&
      address.addresses[0].state &&
      address.addresses[0].state.value,
    ppmeClaimUrl: transaction.ppmeClaimUrl,
    showPPMeIframe: transaction.showPPMeIframe,
    selectedFundingOption: transaction.selectedFundingOption,
    complianceInfo: transaction.complianceInfo,
    isNativeApp: server.isNativeApp,
    isAndroid: server.isAndroid,
    isiPhone: server.isiPhone,
    isBusinessUser: server.businessAccountInfo.isBusinessUser,
    userFirstName: server.userFirstName,
    userThumbnailUrl: server.userThumbnailUrl,
    currencyCode: amount.currencyCode,
    language: user.language,
    liveHostName: server.liveHostName,
  })
)(OutlookSuccess)
